
import * as React from 'react';
import { copy } from '@dailydeck/shared/copy';
import { useNotes } from '../../hooks/useNotes.js';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../../components/ui/Card.js';
import { SkeletonRow } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { Button } from '../../components/ui/Button.js';
import { QuickAdd } from '../../components/ui/QuickAdd.js';

export function Notes(): JSX.Element {
  React.useEffect(() => {
    document.title = `DailyDeck — ${copy.notes.pageTitle}`;
  }, []);

  const query = useNotes();

  return (
    <div className="space-y-6" data-testid="notes-route">
      <header>
        <h1 className="text-title-1 font-semibold text-text-primary">
          {copy.notes.pageTitle}
        </h1>
        <p className="text-body text-text-secondary mt-1">
          {copy.notes.pageSubtitle}
        </p>
        <div className="mt-3">
          <QuickAdd label="+ New note" fields={[{ key: 'title', placeholder: 'Title' }]} endpoint="/api/modules/notes/entries" queryKey={['notes', 'entries']} />
        </div>
      </header>

      {query.isPending && (
        <div className="rounded-lg border border-border-subtle bg-surface-raised" aria-label={copy.notes.loadingLabel}>
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      )}

      {query.isError && (
        <Card data-testid="notes-error">
          <CardHeader>
            <CardTitle>{copy.notes.errorTitle}</CardTitle>
            <CardDescription>{copy.errors.network_unavailable}</CardDescription>
          </CardHeader>
          <CardFooter>
            <Button variant="secondary" onClick={() => void query.refetch()}>
              {copy.notes.retry}
            </Button>
          </CardFooter>
        </Card>
      )}

      {query.isSuccess && query.data.length === 0 && (
        <EmptyState title={copy.notes.emptyTitle} description={copy.notes.emptyBody} />
      )}

      {query.isSuccess && query.data.length > 0 && (
        <div className="space-y-2">
          {query.data.map((note) => (
            <Card key={note.id}>
              <CardContent className="py-4">
                <h3 className="text-body-strong font-medium text-text-primary">
                  {note.title || 'Untitled'}
                </h3>
                {note.body && (
                  <p className="text-body text-text-secondary mt-1 line-clamp-3 whitespace-pre-wrap">
                    {note.body}
                  </p>
                )}
                {note.tags && note.tags !== '[]' && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(JSON.parse(note.tags) as string[]).map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex rounded-full bg-accent-subtle px-2 py-0.5 text-micro text-accent-default"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-caption text-text-muted mt-2">
                  {new Date(note.created_at).toLocaleDateString()}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
